import React, { useState, useEffect } from "react";
import axios from "axios";
import { TrendingUp, TrendingDown } from "lucide-react";

const API = `${window.location.origin}/api`;

export const InvoiceProfitabilityWidget = ({ authHeaders, jobId, revenueOverride = null }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/jobs/${jobId}/profitability`, authHeaders);
        if (!cancelled) setData(res.data);
      } catch (e) {
        // 404 = stale job_id (deleted enquiry) → hide widget silently. Other codes =
        // log to console so we notice backend drift without spamming users.
        if (!cancelled && e.response && e.response.status !== 404) {
          console.warn('[InvoiceProfitabilityWidget]', e.response.status, e.response.data);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [jobId, authHeaders]);

  if (!data) return null;

  // If editor gives us a live invoice total, use that as revenue instead of the server's cached value
  const revenue = revenueOverride !== null ? revenueOverride : (data.revenue || 0);
  const materialCost = data.material_cost_received || 0;
  const profit = revenue - materialCost;
  const margin = revenue > 0 ? ((profit / revenue) * 100).toFixed(1) : 0;
  const positive = profit >= 0;

  return (
    <div
      className={`rounded-lg border p-3 ${positive ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
      data-testid="invoice-profitability-widget"
    >
      <div className="flex items-center gap-2 text-sm font-medium mb-2">
        {positive ? <TrendingUp className="h-4 w-4 text-green-700" /> : <TrendingDown className="h-4 w-4 text-red-700" />}
        Invoice profitability (live)
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-muted-foreground">Invoice total</div>
          <div className="font-semibold text-sm" data-testid="invoice-profit-revenue">£{revenue.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Received materials</div>
          <div className="font-semibold text-sm" data-testid="invoice-profit-materials">£{materialCost.toFixed(2)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Net profit</div>
          <div className={`font-bold text-sm ${positive ? 'text-green-700' : 'text-red-700'}`} data-testid="invoice-profit-net">
            £{profit.toFixed(2)} <span className="font-normal text-xs">({margin}%)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceProfitabilityWidget;
